import React, { useEffect, useRef, useState, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useAuth } from '../context/AuthContext';
import { useAdminWs, useAdminWsConnected } from '../context/AdminWsContext';
import { RefreshCw } from 'lucide-react';

function makeIcon(online) {
  return L.divIcon({
    className: '',
    html: `<div class="${online ? 'map-marker-online' : 'map-marker-offline'}"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
    popupAnchor: [0, -14],
  });
}

// Маркер фактической позиции (Wi-Fi-позиционирование с киоска)
const autoIcon = L.divIcon({
  className: '',
  html: '<div class="map-marker-auto"></div>',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
  popupAnchor: [0, -10],
});

// Расстояние между точками в метрах (haversine) — для подписи расхождения
function distMeters(lat1, lng1, lat2, lng2) {
  const R = 6371000;
  const toRad = (d) => d * Math.PI / 180;
  const a = Math.sin(toRad(lat2 - lat1) / 2) ** 2 +
            Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(toRad(lng2 - lng1) / 2) ** 2;
  return Math.round(2 * R * Math.asin(Math.sqrt(a)));
}

const fmtDist = (m) => m >= 1000 ? `${(m / 1000).toFixed(1)} км` : `${m} м`;

export const MapView = () => {
  const { fetchAuthorized } = useAuth();
  const wsOk = useAdminWsConnected();
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const mapContainerRef = useRef(null);
  const leafletRef      = useRef(null);
  const markersRef      = useRef({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchAuthorized('/api/dashboard/devices');
      const d   = await res.json();
      if (d.success) setDevices(d.devices);
    } catch {}
    finally { setLoading(false); }
  }, [fetchAuthorized]);

  // Init Leaflet once when component mounts
  useEffect(() => {
    if (!mapContainerRef.current || leafletRef.current) return;

    leafletRef.current = L.map(mapContainerRef.current, {
      center: [41.299, 69.240],
      zoom: 12,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap contributors',
      maxZoom: 19,
    }).addTo(leafletRef.current);

    return () => {
      leafletRef.current?.remove();
      leafletRef.current = null;
      markersRef.current = {};
    };
  }, []);

  // Rebuild markers when devices array changes
  useEffect(() => {
    const map = leafletRef.current;
    if (!map) return;

    Object.values(markersRef.current).forEach(m => m.remove());
    markersRef.current = {};

    devices.forEach(device => {
      const hasAdmin = device.lat != null && device.lng != null;
      const hasAuto  = device.auto_lat != null && device.auto_lng != null;
      if (!hasAdmin && !hasAuto) return;

      // Расхождение «должен стоять» ↔ «реально находится»
      const drift = hasAdmin && hasAuto
        ? distMeters(device.lat, device.lng, device.auto_lat, device.auto_lng)
        : null;
      const driftLine = drift != null
        ? `<div style="margin-top:6px;font-size:11px;color:${drift > 500 ? '#f87171' : '#94a3b8'};">
             ${drift > 500 ? '⚠ ' : ''}Расхождение с фактической позицией: ${fmtDist(drift)}
           </div>`
        : '';

      // Основной пин — админская точка; если её нет, показываем фактическую
      if (hasAdmin) {
        const marker = L.marker([device.lat, device.lng], {
          icon: makeIcon(device.is_online),
        }).addTo(map);

        marker.bindPopup(`
          <div style="font-family:'Inter',sans-serif;min-width:180px;">
            <div style="font-weight:700;font-size:13px;margin-bottom:6px;color:#e2e8f0;">
              ${device.name || device.machine_name || device.device_id}
            </div>
            <div style="display:flex;align-items:center;gap:6px;font-size:12px;">
              <span style="display:inline-block;width:8px;height:8px;border-radius:50%;
                background:${device.is_online ? '#4ade80' : '#f87171'};"></span>
              <span style="color:${device.is_online ? '#4ade80' : '#f87171'};">
                ${device.is_online ? 'Онлайн' : 'Оффлайн'}
              </span>
            </div>
            <div style="margin-top:8px;font-size:11px;color:#94a3b8;">${device.lat}, ${device.lng}</div>
            ${driftLine}
          </div>
        `);

        markersRef.current[device.device_id] = marker;
      }

      // Фактическая позиция (Wi-Fi) — отдельный маленький маркер
      if (hasAuto) {
        const autoMarker = L.marker([device.auto_lat, device.auto_lng], { icon: autoIcon }).addTo(map);
        autoMarker.bindPopup(`
          <div style="font-family:'Inter',sans-serif;min-width:180px;">
            <div style="font-weight:700;font-size:13px;margin-bottom:6px;color:#e2e8f0;">
              📡 ${device.name || device.machine_name || device.device_id}
            </div>
            <div style="font-size:11px;color:#94a3b8;">
              Фактическая позиция (Wi-Fi)<br/>
              ${device.auto_lat.toFixed(6)}, ${device.auto_lng.toFixed(6)}
            </div>
            ${driftLine}
          </div>
        `);
        markersRef.current[`${device.device_id}:auto`] = autoMarker;
        if (!hasAdmin) markersRef.current[device.device_id] = autoMarker;
      }
    });
  }, [devices]);

  // WS: update marker icon on status change without full reload
  const handleWs = useCallback((msg) => {
    if (msg.event === 'device_online') {
      setDevices(prev => prev.map(d =>
        d.device_id === msg.data.device_id ? { ...d, is_online: true, last_seen: new Date().toISOString() } : d
      ));
      const marker = markersRef.current[msg.data.device_id];
      if (marker) marker.setIcon(makeIcon(true));
    }
    if (msg.event === 'device_offline') {
      setDevices(prev => prev.map(d =>
        d.device_id === msg.data.device_id ? { ...d, is_online: false } : d
      ));
      const marker = markersRef.current[msg.data.device_id];
      if (marker) marker.setIcon(makeIcon(false));
    }
  }, []);

  useAdminWs(handleWs);
  useEffect(() => { load(); }, [load]);

  const flyTo = (lat, lng) => {
    leafletRef.current?.flyTo([lat, lng], 16, { duration: 1.2 });
  };

  const withCoords = devices.filter(d =>
    (d.lat != null && d.lng != null) || (d.auto_lat != null && d.auto_lng != null)
  );

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1>Карта</h1>
          <p>Расположение киосков и статус в реальном времени</p>
        </div>
        <div className="page-header-actions">
          <span className={`badge ${wsOk ? 'badge-green' : 'badge-yellow'}`}>
            <span className={`dot ${wsOk ? 'dot-online' : 'dot-warning'}`} style={{ width: '6px', height: '6px' }} />
            {wsOk ? 'Live' : 'Подключение...'}
          </span>
          <button className="btn-icon" onClick={load} disabled={loading} title="Обновить">
            <RefreshCw size={14} className={loading ? 'spin' : ''} />
          </button>
        </div>
      </div>

      {/* Map card */}
      <div className="card" style={{ overflow: 'hidden' }}>
        <div ref={mapContainerRef} id="leaflet-map" />

        {/* Legend */}
        <div className="map-legend">
          <div className="map-legend-item">
            <span className="dot dot-online" />
            <span>Онлайн</span>
          </div>
          <div className="map-legend-item">
            <span className="dot dot-offline" />
            <span>Оффлайн</span>
          </div>
          <div className="map-legend-item">
            <span className="map-marker-auto" style={{ width: '11px', height: '11px', display: 'inline-block' }} />
            <span>Фактическая позиция (Wi-Fi)</span>
          </div>
          <span className="text-dim" style={{ marginLeft: 'auto', fontSize: '11px' }}>
            Нажмите на маркер для деталей · OpenStreetMap
          </span>
        </div>
      </div>

      {/* Kiosk list */}
      <div className="card" style={{ marginTop: '14px', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid var(--glass-border)', fontWeight: '600', fontSize: '13px' }}>
          Точки установки
        </div>
        <table>
          <thead>
            <tr>
              <th>Киоск</th>
              <th>Статус</th>
              <th>Координаты</th>
            </tr>
          </thead>
          <tbody>
            {withCoords.length > 0 ? withCoords.map(d => {
              const hasAdmin = d.lat != null && d.lng != null;
              const hasAuto  = d.auto_lat != null && d.auto_lng != null;
              const drift = hasAdmin && hasAuto ? distMeters(d.lat, d.lng, d.auto_lat, d.auto_lng) : null;
              return (
                <tr
                  key={d.device_id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => flyTo(hasAdmin ? d.lat : d.auto_lat, hasAdmin ? d.lng : d.auto_lng)}
                  title="Перейти на карте"
                >
                  <td>
                    <div className="fw-600">{d.name || d.machine_name || 'Unnamed'}</div>
                    <div className="mono text-dim" style={{ fontSize: '11px' }}>{d.device_id?.slice(0, 12)}…</div>
                  </td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                      <span className={`dot ${d.is_online ? 'dot-online' : 'dot-offline'}`} />
                      <span style={{ fontSize: '13px', color: d.is_online ? 'var(--green)' : 'var(--text-2)' }}>
                        {d.is_online ? 'Онлайн' : 'Оффлайн'}
                      </span>
                    </div>
                    {drift != null && drift > 500 && (
                      <div style={{ fontSize: '11px', color: 'var(--red)', marginTop: '2px' }}>
                        ⚠ не на месте ({fmtDist(drift)})
                      </div>
                    )}
                  </td>
                  <td className="mono text-dim" style={{ fontSize: '11px' }}>
                    {hasAdmin ? `${d.lat}, ${d.lng}` : '—'}
                    {hasAuto && (
                      <div style={{ marginTop: '2px' }}>
                        📡 {d.auto_lat.toFixed(6)}, {d.auto_lng.toFixed(6)}
                      </div>
                    )}
                  </td>
                </tr>
              );
            }) : (
              <tr>
                <td colSpan={3} className="empty">
                  {loading
                    ? 'Загрузка...'
                    : 'Нет устройств с координатами. Задайте точку через кнопку «Локация» в разделе Киоски или при создании киоска.'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
